<template>
  <div class="supervisor-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">ANIMATION REPAS</h1>
      <p class="dashboard-subtitle">Service du midi</p>
    </div>

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Feedback global -->
      <div class="card card-featured" @click="showGlobalFeedback = true">
        <div class="card-header">
          <h3 class="card-title">😊 Appréciation globale</h3>
          <span class="badge badge-green">{{ getRatingLabel(globalRating) }}</span>
        </div>
        <p class="card-description">Donner votre évaluation du repas</p>
        <button class="card-link">Évaluer →</button>
      </div>

      <!-- Card 2: Feedback détaillé -->
      <div class="card" @click="showDetailedFeedback = true">
        <div class="card-header">
          <h3 class="card-title">🍽️ Détail par plat</h3>
          <span class="badge badge-blue">{{ dishes.length }} plats</span>
        </div>
        <p class="card-description">Noter chaque plat individuellement</p>
        <button class="card-link">Noter →</button>
      </div>

      <!-- Card 3: Contexte -->
      <div class="card" @click="showContextForm = true">
        <div class="card-header">
          <h3 class="card-title">🌤️ Contexte du jour</h3>
        </div>
        <p class="card-description">Météo, ambiance et observations</p>
        <button class="card-link">Renseigner →</button>
      </div>

      <!-- Card 4: Communications -->
      <div class="card" @click="showCommunications = true">
        <div class="card-header">
          <h3 class="card-title">📢 Infos du jour</h3>
          <span class="badge badge-orange" v-if="messages.length > 0">{{ messages.length }}</span>
        </div>
        <p class="card-description">Messages et alertes importantes</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 5: Afficher aux enfants -->
      <div class="card card-highlight" @click="showWasteToKids = true">
        <div class="card-header">
          <h3 class="card-title">👀 Montrer aux enfants</h3>
        </div>
        <p class="card-description">Visualisation ludique du gaspillage</p>
        <button class="card-link">Afficher →</button>
      </div>

      <!-- Card 6: Ajouter commentaire/photo -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">💬 Ajouter</h3>
        </div>
        <div class="action-buttons">
          <button @click="addComment" class="action-btn">
            <span class="action-icon">💬</span>
            <span>Commentaire</span>
          </button>
          <button @click="takePhoto" class="action-btn">
            <span class="action-icon">📸</span>
            <span>Photo</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Appréciation globale -->
    <div v-if="showGlobalFeedback" class="modal-overlay" @click="showGlobalFeedback = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showGlobalFeedback = false">✕</button>
        <h2 class="modal-title">😊 Appréciation globale</h2>
        <p class="modal-subtitle">Comment s'est passé le repas ?</p>

        <div class="rating-selector">
          <button
            v-for="(emoji, idx) in emojis"
            :key="idx"
            @click="globalRating = idx + 1"
            :class="{ active: globalRating === idx + 1 }"
            class="rating-btn"
          >
            <span class="rating-emoji">{{ emoji }}</span>
            <span class="rating-label">{{ getRatingLabelByIndex(idx) }}</span>
          </button>
        </div>

        <button v-if="globalRating" @click="saveGlobalRating" class="btn btn-primary">Enregistrer</button>
      </div>
    </div>

    <!-- Modal: Détail par plat -->
    <div v-if="showDetailedFeedback" class="modal-overlay" @click="showDetailedFeedback = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showDetailedFeedback = false">✕</button>
        <h2 class="modal-title">🍽️ DÉTAIL PAR PLAT</h2>
        <div class="dishes-list">
          <div v-for="(dish, idx) in dishes" :key="idx" class="dish-card">
            <div class="flex justify-between items-start mb-3">
              <div>
                <h4 class="dish-name">{{ dish.name }}</h4>
                <p class="text-xs text-gray-500">{{ dish.category }}</p>
              </div>
            </div>
            <div class="mb-3">
              <p class="text-xs text-gray-600 mb-1">Appréciation</p>
              <div class="dish-ratings">
                <button
                  v-for="(emoji, i) in emojis"
                  :key="i"
                  @click="updateDishRating(idx, i + 1)"
                  :class="['rating-mini-btn', { active: dish.rating === i + 1 }]"
                >
                  {{ emoji }}
                </button>
                <span class="rating-percent">{{ dish.rating ? getRatingLabel(dish.rating) : "" }}</span>
              </div>
            </div>
            <!-- <div class="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p class="text-xs text-gray-600 mb-1">Servi</p>
                <input type="range" v-model="dish.served" min="0" max="100" class="w-full" />
                <p class="text-xs text-gray-600 mt-1">{{ dish.served }}%</p>
              </div>
              <div>
                <p class="text-xs text-gray-600 mb-1">Restes</p>
                <input type="range" v-model="dish.leftover" min="0" max="100" class="w-full" />
                <p class="text-xs text-gray-600 mt-1">{{ dish.leftover }}%</p>
              </div>
            </div> -->
            <div v-if="dish.comment" class="dish-comment">💬 "{{ dish.comment }}"</div>
            <button @click="editDishComment(idx)" class="text-xs text-blue-600 mt-2 hover:underline">
              {{ dish.comment ? "Modifier" : "Ajouter" }} un commentaire
            </button>
          </div>
        </div>
        <button @click="showDetailedFeedback = false" class="btn btn-primary">Terminé</button>
      </div>
    </div>

    <!-- Modal: Contexte -->
    <div v-if="showContextForm" class="modal-overlay" @click="showContextForm = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showContextForm = false">✕</button>
        <h2 class="modal-title">🌤️ Contexte du jour</h2>

        <div class="detail-section">
          <h3 class="section-title">Météo</h3>
          <div class="weather-selector">
            <button
              v-for="w in weatherOptions"
              :key="w.key"
              @click="weather = w.key"
              :class="{ active: weather === w.key }"
              class="weather-btn"
            >
              <span class="weather-icon">{{ w.icon }}</span>
              <span class="weather-label">{{ w.label }}</span>
            </button>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Température</h3>
          <div class="temperature-input">
            <input v-model.number="temperature" type="number" class="input-field" placeholder="22" />
            <span class="temperature-unit">°C</span>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Niveau sonore</h3>
          <div class="noise-selector">
            <button
              v-for="n in noiseOptions"
              :key="n.key"
              @click="noiseLevel = n.key"
              :class="{ active: noiseLevel === n.key }"
              class="noise-btn"
            >
              <span class="noise-icon">{{ n.icon }}</span>
              <span class="noise-label">{{ n.label }}</span>
            </button>
          </div>
        </div>

        <button @click="saveContext" class="btn btn-primary">Enregistrer</button>
      </div>
    </div>

    <!-- Modal: Communications -->
    <div v-if="showCommunications" class="modal-overlay" @click="showCommunications = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showCommunications = false">✕</button>
        <h2 class="modal-title">📢 Infos du jour</h2>

        <div class="messages-list">
          <div v-for="msg in messages" :key="msg.id" class="message-card" :class="'message-' + msg.type">
            <div class="message-header">
              <strong>{{ msg.title }}</strong>
              <span class="message-source">{{ msg.source }}</span>
            </div>
            <p class="message-content">{{ msg.content }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal full screen: Gaspillage pour enfants -->
    <div v-if="showWasteToKids" class="fullscreen-modal">
      <button @click="showWasteToKids = false" class="fullscreen-close">✕ Fermer</button>
      <waste-visualization mode="display" />
    </div>
  </div>
</template>

<script>
import WasteVisualization from "@/components/WasteVisualization.vue"

export default {
  name: "SupervisorDashboard2",
  components: {
    WasteVisualization,
  },
  data() {
    return {
      showGlobalFeedback: false,
      showDetailedFeedback: false,
      showContextForm: false,
      showCommunications: false,
      showWasteToKids: false,
      emojis: ["😞", "😕", "😐", "😊", "😍"],
      globalRating: null,
      noiseLevel: 3,
      noiseOptions: [
        { key: 1, icon: "😊", label: "Calme" },
        { key: 2, icon: "🙂", label: "Plutôt calme" },
        { key: 3, icon: "😐", label: "Normal" },
        { key: 4, icon: "😟", label: "Bruyant" },
        { key: 5, icon: "😱", label: "Très bruyant" },
      ],
      temperature: 22,
      weather: "sunny",
      weatherOptions: [
        { key: "sunny", icon: "☀️", label: "Ensoleillé" },
        { key: "cloudy", icon: "☁️", label: "Nuageux" },
        { key: "rainy", icon: "🌧️", label: "Pluvieux" },
      ],
      dishes: [
        { name: "Poulet rôti", ratings: [3, 7, 20, 40, 30], rating: null, comment: '"Trop sec"' },
        { name: "Riz", ratings: [5, 15, 30, 35, 15], rating: null, comment: '"Portion trop grande"' },
        { name: "Haricots verts", ratings: [3, 7, 20, 45, 25], rating: null, comment: "" },
        { name: "Yaourt", ratings: [2, 3, 15, 40, 40], rating: null, comment: "" },
      ],
      messages: [
        {
          id: 1,
          type: "absence",
          title: "Sortie scolaire CM2",
          content: "28 élèves absents - Visite au musée",
          source: "École",
        },
        {
          id: 2,
          type: "info",
          title: "Nouveau régime",
          content: "2 nouveaux élèves végétariens en CE1",
          source: "Infirmerie",
        },
      ],
    }
  },
  methods: {
    getRatingLabel(rating) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return rating ? labels[rating - 1] : "Non évalué"
    },
    getRatingLabelByIndex(idx) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return labels[idx]
    },
    updateDishRating(idx, rating) {
      this.dishes[idx].rating = rating
    },
    editDishComment(idx) {
      const newComment = prompt("Commentaire pour " + this.dishes[idx].name + ":", this.dishes[idx].comment || "")
      if (newComment !== null) {
        this.dishes[idx].comment = newComment
      }
    },
    saveGlobalRating() {
      alert(`Appréciation globale enregistrée : ${this.getRatingLabel(this.globalRating)}`)
      this.showGlobalFeedback = false
    },
    saveContext() {
      alert("Contexte enregistré")
      this.showContextForm = false
    },
    addComment() {
      const comment = prompt("Votre commentaire :")
      if (comment) {
        alert(`Commentaire ajouté : ${comment}`)
      }
    },
    takePhoto() {
      alert("Ouverture de l'appareil photo")
    },
  },
}
</script>

<style scoped>
.supervisor-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-featured {
  border: 2px solid #f97316;
  background: linear-gradient(135deg, #fff7ed 0%, white 100%);
}

.card-highlight {
  border: 2px solid #8b5cf6;
  background: linear-gradient(135deg, #faf5ff 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.card-link {
  display: inline-flex;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.action-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #eff6ff;
  border-color: #2563eb;
}

.action-icon {
  font-size: 2rem;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Rating selector */
.rating-selector {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.rating-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.rating-btn:hover {
  background: #eff6ff;
}

.rating-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
  transform: scale(1.05);
}

.rating-emoji {
  font-size: 2.5rem;
}

.rating-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

/* Dishes list */
.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.dish-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
}

.dish-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.dish-ratings {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.rating-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.rating-mini-btn {
  font-size: 1.5rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.rating-mini-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  transform: scale(1.1);
}

.rating-percent {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.dish-comment {
  font-size: 0.875rem;
  color: #f97316;
  font-style: italic;
  margin: 0.75rem 0 0 0;
}

/* Weather selector */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.weather-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.weather-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.weather-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
}

.weather-icon {
  font-size: 2.5rem;
}

.weather-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.temperature-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.input-field {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
}

.input-field:focus {
  outline: none;
  border-color: #2563eb;
}

.temperature-unit {
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
}

.noise-selector {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.noise-btn {
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  transition: width 0.5s ease;
  cursor: pointer;
}
.noise-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
}
.noise-icon {
  font-size: 1.5rem;
}
.noise-label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Messages */
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.message-card {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.message-absence {
  background: #fef2f2;
  border-color: #ef4444;
}

.message-info {
  background: #eff6ff;
  border-color: #2563eb;
}

.message-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.message-source {
  font-size: 0.75rem;
  color: #6b7280;
}

.message-content {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
}

/* Fullscreen modal */
.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}

.fullscreen-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  z-index: 2001;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .rating-selector {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
